import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useStructureModeling } from "../../structureModelingContext";

export function SpecHeader() {
  const { id } = useParams();

  const [spec, setSpec] = useState("");
  const [rev, setRev] = useState("");

  const [uniqueSpecs, setUniqueSpecs] = useState([]);
  const [uniqueRevs, setUniqueRevs] = useState([]);

  const {
    specTable,
    addSlectedSpec,
    selectedSpecs,
    removeSelectedSpec,
    clientName,
  } = useStructureModeling();

  const [isAlreadySelected, setIsAlreadySelected] = useState(false);

  useEffect(() => {
    setIsAlreadySelected(
      selectedSpecs.some(
        (item) => item.spec === spec && item.rev.toString() === rev.toString(),
      ),
    );
  }, [selectedSpecs, rev, spec]);

  useEffect(() => {
    if (!specTable || !clientName) return;

    // First, check if ANY item has this client
    const hasAnyClientMatch = specTable.some((item) => {
      try {
        const cn =
          typeof item.clientName === "string"
            ? JSON.parse(item.clientName)
            : item.clientName;
        return Array.isArray(cn) && cn.includes(clientName);
      } catch {
        return false;
      }
    });

    const clientFiltered = specTable.filter((item) => {
      try {
        const cn =
          typeof item.clientName === "string"
            ? JSON.parse(item.clientName)
            : item.clientName;

        if (!Array.isArray(cn)) return false;

        const hasClient = cn.includes(clientName);
        const hasDemo = cn.includes("DEMO");

        // If we found any client match elsewhere, ONLY show items with this client
        if (hasAnyClientMatch) {
          return hasClient;
        }

        // Otherwise, fall back to demo items
        return hasDemo;
      } catch {
        return false;
      }
    });

    // ... rest of your logic
    let filteredSpecs = clientFiltered;
    let filteredRevs = clientFiltered;

    if (rev !== "") {
      filteredSpecs = clientFiltered.filter(
        (item) => item.Rev.toString() === rev.toString(),
      );
    }

    if (spec !== "") {
      filteredRevs = clientFiltered.filter((item) => item.Spec === spec);
    }

    setUniqueSpecs([...new Set(filteredSpecs.map((item) => item.Spec))]);
    setUniqueRevs([
      ...new Set(filteredRevs.map((item) => item.Rev.toString())),
    ]);
  }, [spec, rev, clientName, specTable]);

  // Reset spec/rev if no longer valid after clientName resolves
  useEffect(() => {
    if (spec && !uniqueSpecs.includes(spec)) setSpec("");
  }, [uniqueSpecs]);

  useEffect(() => {
    if (rev && !uniqueRevs.includes(rev)) setRev("");
  }, [uniqueRevs]);

  function handleAddOrRemove() {
    if (!spec || !rev) return;

    const label = spec + " - Rev " + rev;

    if (isAlreadySelected) {
      removeSelectedSpec({ label, spec, rev });
    } else {
      addSlectedSpec({ label, spec, rev });
    }
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={4}
      justifyContent="center"
      sx={{ display: "flex", py: 1, px: 2 }}
    >
      <FormControl size="small">
        <InputLabel>Spec</InputLabel>
        <Select
          value={spec}
          onChange={(e) => setSpec(e.target.value)}
          label="Spec"
          sx={{ minWidth: 140 }}
        >
          {uniqueSpecs.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small">
        <InputLabel>Revision</InputLabel>
        <Select
          value={rev}
          onChange={(e) => setRev(e.target.value)}
          label="Revision"
          sx={{ minWidth: 140 }}
        >
          {uniqueRevs.map((r) => (
            <MenuItem key={r} value={r}>
              {r}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant={isAlreadySelected ? "outlined" : "contained"}
        color={isAlreadySelected ? "error" : "primary"}
        onClick={handleAddOrRemove}
        disabled={!spec || !rev}
        sx={{ minWidth: 120 }}
      >
        {isAlreadySelected ? "Remove Spec" : "Add Spec"}
      </Button>
    </Box>
  );
}
